import { useMutation } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { showNotification } from '@mantine/notifications';
import { updateEvidenceField } from '../api/evidences';
import { useState } from 'react';

interface UseEvidenceFieldUpdateParams {
  evidenceId: string;
  onSuccessCallback?: () => void;
}

interface UpdateFieldPayload {
  fieldPath: string;
  value: any;
  fieldType?: 'text' | 'number' | 'date' | 'select';
}

interface UseEvidenceFieldUpdateReturn {
  updateField: (payload: UpdateFieldPayload) => void;
  isUpdating: boolean;
  updatingFieldPath: string | null;
  isFieldUpdating: (fieldPath: string) => boolean;
  error: Error | null;
}

/**
 * Hook for updating evidence fields
 * Uses local component state only - no optimistic cache updates
 * Updates will reflect after natural refetches (navigation, refresh)
 * Tracks which specific field is being updated for granular loading states
 */
export const useEvidenceFieldUpdate = ({
  evidenceId,
  onSuccessCallback,
}: UseEvidenceFieldUpdateParams): UseEvidenceFieldUpdateReturn => {
  const auth0 = useAuth0();
  const [updatingFieldPath, setUpdatingFieldPath] = useState<string | null>(null);

  const updateFieldMutation = useMutation({
    mutationFn: async (payload: UpdateFieldPayload) => {
      return await updateEvidenceField(
        evidenceId,
        payload.fieldPath,
        payload.value,
        auth0
      );
    },
    onMutate: (payload: UpdateFieldPayload) => {
      // Track which field is being updated
      setUpdatingFieldPath(payload.fieldPath);
    },
    onSuccess: () => {
      setUpdatingFieldPath(null);
      showNotification({
        title: 'Saved',
        message: 'Field updated successfully',
        color: 'teal',
        autoClose: 2000,
      });
      
      if (onSuccessCallback) {
        onSuccessCallback();
      }
    },
    onError: (error: any) => {
      setUpdatingFieldPath(null);
      showNotification({
        title: 'Error',
        message: error?.message || 'Failed to update field',
        color: 'red',
        autoClose: 3000,
      });
    },
  });

  const isFieldUpdating = (fieldPath: string) => {
    return updateFieldMutation.isPending && updatingFieldPath === fieldPath;
  };

  return {
    updateField: updateFieldMutation.mutate,
    isUpdating: updateFieldMutation.isPending,
    updatingFieldPath,
    isFieldUpdating,
    error: updateFieldMutation.error,
  };
};

